import React, { useMemo } from 'react';
import { Section } from 'new/components/settings/VendorBuilder/components/Section';
import { contentTypes, requestModes } from 'new/components/settings/VendorBuilder/selectTypes';
import { FormBuilder } from 'new/components/settings/VendorBuilder/FormControlBuilder';
import Typography from 'new/elements/Typography';
import { Stack } from '@mui/material';
interface VendorTestProps {
  templateIndex: number;
}

const fields = (index) => [
  {
    type: 'checkbox',
    label: (
      <>
        Skip Validation
        <code>auth_template[].config["skip.validation"]</code>
      </>
    ),
    required: true,
    name: `auth_templates[${index}].config["skip.validation"]`,
    defaultValue: false,
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          Skip Validation (<code>auth_template[].config["skip.validation"]</code>)
        </Typography>
        <Typography variant="body2">
          Turn this option on to skip validation of credential when the credential is being created.
          Note that credential validation is still done when the credential is used as part of a
          source/destination. However, sometimes some APIs don't have a good endpoint that can be
          used for just testing if the credential information is accurate or not. This optional
          property helps avoid setting test.url properties for such API"
        </Typography>
      </Stack>
    ),
  },
  {
    dependsOn: {
      [`auth_templates[${index}].config["skip.validation"]`]: false,
    },
    type: 'text',
    label: (
      <>
        Credential Validation URL <code>auth_template[].config["test.url"]</code>
      </>
    ),
    required: true,
    name: `auth_templates[${index}].config["test.url"]`,
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          Credential Validation URL <code>auth_template[].config["test.url"]</code>
        </Typography>
        <Typography variant="body2">
          Enter a URL from this API vendor that should be used to check whether or not the
          authentication mechanism works.
          <ol>
            <li>
              This does not need to be the same URL as the final endpoint that you wish to use.
              <li>
                We recommend using an endpoint that does not incur a noticeable response delay from
                the API server. For example, fetch only one item if accessing an endpoint that sends
                an array of items.
              </li>
            </li>
          </ol>
        </Typography>
      </Stack>
    ),
  },
  {
    dependsOn: {
      [`auth_templates[${index}].config["skip.validation"]`]: false,
    },
    type: 'select',
    defaultValue: requestModes[0].value,
    label: (
      <>
        Credential Validation API Method <code>auth_template[].config["test.method"]</code>
      </>
    ),
    required: true,
    items: requestModes,
    name: `auth_templates[${index}].config["test.method"]`,
  },
  {
    dependsOn: {
      [`auth_templates[${index}].config["skip.validation"]`]: false,
    },
    type: 'select',
    defaultValue: contentTypes[0].value,
    label: (
      <>
        Content type <code>auth_template[].config["test.content.type"]</code>
      </>
    ),
    required: true,
    items: contentTypes,
    name: `auth_templates[${index}].config["test.content.type"]`,
  },
];

export const AuthTemplateTest = (props: VendorTestProps) => {
  const { templateIndex: index } = props;

  const testFields = useMemo(() => fields(index), [index]);

  return (
    <Section title={<div>Template #{index + 1}: Testing Info </div>} gap="16px">
      <Typography variant="body2">
        Configure this section for information on how the generated credential should be validated.
        <br />
        <strong>Note:</strong>
        <ol>
          <li>
            We recently added suppport for bypassing credential validation if needed. Use that
            option if there is no good url for validation.
          </li>
          <li>
            The auth <code>test.url</code> should be an inexpensive call to the vendor API that will
            be used to make a test call for authentication success. This could be something as
            simple as grabbing a users profile.
          </li>
        </ol>
      </Typography>

      <FormBuilder fields={testFields} />
    </Section>
  );
};
